import React from 'react';
import type { NewsItem as NewsItemType } from '../types/news';
import { formatDate } from '../utils/dateUtils';

interface NewsItemProps {
  news: NewsItemType;
  onClick: (news: NewsItemType) => void;
  featured?: boolean;
  mobile?: boolean;
}

export const NewsItem: React.FC<NewsItemProps> = ({
  news,
  onClick,
  featured = false,
  mobile = false,
}) => {
  const handleClick = () => onClick(news);

  // Mobile flat list row — no card, no image, just headline
  if (mobile) {
    return (
      <div
        className="flex items-start gap-3 px-4 py-3 cursor-pointer active:bg-gray-50 transition-colors"
        onClick={handleClick}
        role="article"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && handleClick()}
      >
        <div className="w-0.5 self-stretch bg-red-700 flex-shrink-0 rounded-sm opacity-60"></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span className="text-xs font-semibold text-red-700 uppercase tracking-wide truncate">{news.source}</span>
            <time className="text-xs text-gray-400 flex-shrink-0">{formatDate(news.publishedDate)}</time>
          </div>
          <h2 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">{news.title}</h2>
        </div>
      </div>
    );
  }

  if (featured) {
    return (
      <article
        className="news-item news-item--featured bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden group"
        onClick={handleClick}
        role="article"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && handleClick()}
      >
        <div className="md:flex">
          {news.image && (
            <div className="md:w-1/2 lg:w-2/5 relative overflow-hidden">
              <img
                src={news.image}
                alt={news.title}
                className="w-full h-64 md:h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute top-4 left-4">
                <span className="inline-block px-3 py-1 bg-red-700 text-white text-xs font-bold uppercase tracking-wider rounded shadow">
                  Breaking News
                </span>
              </div>
            </div>
          )}
          <div className="p-6 md:w-1/2 lg:w-3/5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-700 rounded-full animate-pulse"></div>
                  <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">{news.source}</span>
                </div>
                <time dateTime={news.publishedDate} className="text-gray-500 text-xs bg-gray-100 px-2 py-1 rounded-full">
                  {formatDate(news.publishedDate)}
                </time>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight mb-4 group-hover:text-blue-800 transition-colors duration-300">
                {news.title}
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                {news.summary || news.content?.substring(0, 200) + '...'}
              </p>
            </div>
            <footer className="flex items-center justify-between pt-4 mt-4 border-t border-gray-200">
              <span className="text-blue-700 font-bold text-sm group-hover:translate-x-1 transition-transform duration-200">
                Read Full Story →
              </span>
              {news.categories && news.categories.length > 0 && (
                <div className="flex gap-2">
                  {news.categories.slice(0, 2).map((category, index) => (
                    <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full border border-gray-200">
                      {category}
                    </span>
                  ))}
                </div>
              )}
            </footer>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      className="news-item bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden group hover:border-blue-300"
      onClick={handleClick}
      role="article"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && handleClick()}
    >
      {news.image && (
        <div className="relative h-44 overflow-hidden">
          <img
            src={news.image}
            alt={news.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute top-3 right-3">
            <div className="bg-white/90 backdrop-blur-sm rounded-full px-2 py-1">
              <span className="text-xs font-bold text-gray-800">{news.source}</span>
            </div>
          </div>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
            <span className="text-xs font-bold text-gray-900 uppercase tracking-wide">{news.source}</span>
          </div>
          <time dateTime={news.publishedDate} className="text-gray-400 text-xs">{formatDate(news.publishedDate)}</time>
        </div>
        <h2 className="text-sm font-bold text-gray-900 leading-snug mb-2 line-clamp-2 group-hover:text-blue-700 transition-colors duration-200">
          {news.title}
        </h2>
        <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-3">
          {news.summary || news.content?.substring(0, 100) + '...'}
        </p>
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span className="text-blue-700 font-semibold text-xs group-hover:translate-x-0.5 transition-transform duration-200">Read More →</span>
          {news.author && <span className="text-gray-400 text-xs">By {news.author}</span>}
        </div>
      </div>
    </article>
  );
};
